'use client';
import { useRef, useCallback, useMemo } from 'react';

// https://github.com/floating-ui/floating-ui/blob/master/packages/react/src/hooks/useMergeRefs.ts
/**
 * Merges an array of refs into a single memoized callback ref or `null`.
 */
function useMergeRefs(refs) {
    const cleanupRef = useRef(undefined);
    const refEffect = useCallback((instance) => {
        const cleanups = refs.map((ref) => {
            if (ref == null) {
                return null;
            }
            if (typeof ref === 'function') {
                const refCallback = ref;
                // biome-ignore lint/suspicious/noConfusingVoidType: @types-react wants `void` here
                const refCleanup = refCallback(instance);
                return typeof refCleanup === 'function'
                    ? refCleanup
                    : () => {
                        refCallback(null);
                    };
            }
            ref.current = instance;
            return () => {
                ref.current = null;
            };
        });
        return () => {
            for (const refCleanup of cleanups) {
                refCleanup?.();
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, refs);
    return useMemo(() => {
        if (refs.every((ref) => ref == null)) {
            return null;
        }
        return (value) => {
            if (cleanupRef.current) {
                cleanupRef.current();
                cleanupRef.current = undefined;
            }
            if (value != null) {
                cleanupRef.current =
                    refEffect(value);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, refs);
}

export { useMergeRefs };
